import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Repeat, Wallet } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Alert } from '../components/atoms/Alert/Alert';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { EmptyState } from '../components/atoms/EmptyState';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { Select } from '../components/atoms/Select/Select';
import { Stack } from '../components/atoms/Stack/Stack';
import { Text } from '../components/atoms/Typography/Text';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { localToday } from '../components/livestock/livestockDeceased';
import { CategoryTrendChart, CumulativeForecastChart, MonthlyCategoryChart, YearComparisonChart } from '../components/budget/BudgetCharts';
import { BudgetKpis, CategorySwatch, CategoryTable, ExpenseList, MonthTable, RecurringList } from '../components/budget/BudgetPageParts';
import { ExpenseDialog, type ExpenseDialogTarget } from '../components/budget/ExpenseDialog';
import { useMoney } from '../components/budget/useMoney';
import { EnergyOverview } from '../components/controller/EnergyOverview';
import { useEnergySummary } from '../hooks/useController';
import {
  BUDGET_CATEGORIES,
  budgetExpensesKey,
  budgetKey,
  budgetRecurringKey,
  budgetSummaryKey,
  deleteExpense,
  deleteRecurring,
  getBudgetSummary,
  listExpenses,
  listRecurring,
  updateRecurring,
  type BudgetCategory,
  type BudgetExpense,
  type RecurringExpense,
} from '../services/budgetService';
import './BudgetPage.css';

/**
 * Budget: what the tank costs. Expenses by category (salt, food, filter media,
 * electricity, …), monthly and yearly spend, the comparison with the previous
 * year, the trend of each category and the forecast up to 31 December.
 * Recurring costs (every N months) are defined once and booked automatically.
 */
export function BudgetPage() {
  const { t } = useTranslation();
  const money = useMoney();
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const queryClient = useQueryClient();
  const [today] = useState(localToday);
  const [year, setYear] = useState(() => Number(today.slice(0, 4)));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [target, setTarget] = useState<ExpenseDialogTarget>({ kind: 'new', recurring: false });
  const [hidden, setHidden] = useState<ReadonlySet<BudgetCategory>>(new Set());
  const [announcement, setAnnouncement] = useState('');

  const summary = useQuery({
    queryKey: budgetSummaryKey(aquariumId, year),
    queryFn: () => getBudgetSummary(aquariumId, year, today),
    enabled: Boolean(aquariumId),
  });
  const expenses = useQuery({
    queryKey: budgetExpensesKey(aquariumId, year),
    queryFn: () => listExpenses(aquariumId, year, today),
    enabled: Boolean(aquariumId),
  });
  const recurring = useQuery({
    queryKey: budgetRecurringKey(aquariumId),
    queryFn: () => listRecurring(aquariumId),
    enabled: Boolean(aquariumId),
  });
  // Measured electricity from the aquarium controller (#1899) — shown only once a key counts toward it.
  const energy = useEnergySummary(aquariumId || undefined, year);
  const remove = useMutation({
    mutationFn: (expense: BudgetExpense) => deleteExpense(aquariumId, expense.id),
    onSuccess: (_void, expense) => {
      setAnnouncement(t('budget.expenses.deleted', { amount: money.format(expense.amountCents) }));
      void queryClient.invalidateQueries({ queryKey: budgetKey(aquariumId) });
    },
  });

  const recurringChange = useMutation({
    mutationFn: ({ item, action }: { item: RecurringExpense; action: 'end' | 'delete' }) =>
      action === 'end' ? updateRecurring(aquariumId, item.id, { end_on: today }).then(() => undefined) : deleteRecurring(aquariumId, item.id),
    onSuccess: (_void, { item, action }) => {
      const category = t(`budget.categories.${item.category}`);
      setAnnouncement(t(action === 'end' ? 'budget.recurring.endedAnnouncement' : 'budget.recurring.deleted', { category }));
      void queryClient.invalidateQueries({ queryKey: budgetKey(aquariumId) });
    },
  });

  /** Categories with spend in the selected or the previous year, in their fixed order. */
  const usedCategories = useMemo(() => {
    const data = summary.data;
    if (!data) return [];
    return BUDGET_CATEGORIES.filter((c) => data.categories.some((s) => s.category === c && (s.totalCents > 0 || s.previousTotalCents > 0)));
  }, [summary.data]);
  const trendCategories = usedCategories.filter((c) => !hidden.has(c));

  const openDialog = (next: ExpenseDialogTarget) => {
    setTarget(next);
    setDialogOpen(true);
  };
  const openAdd = () => openDialog({ kind: 'new', recurring: false });
  const openAddRecurring = () => openDialog({ kind: 'new', recurring: true });
  const toggleCategory = (c: BudgetCategory) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  const yearOptions = (summary.data?.availableYears ?? [Number(today.slice(0, 4))]).map((y) => ({ value: String(y), label: String(y) }));
  if (!yearOptions.some((o) => o.value === String(year))) yearOptions.push({ value: String(year), label: String(year) });

  let body;
  if (summary.isPending) {
    body = (
      <LiveRegion busy>
        <Text color="muted">{t('budget.loading')}</Text>
      </LiveRegion>
    );
  } else if (summary.isError) {
    body = <LoadErrorState title={t('budget.loadError')} onRetry={() => void summary.refetch()} retryLabel={t('budget.retry')} />;
  } else if (usedCategories.length === 0) {
    body = (
      <Card>
        <EmptyState
          icon={<Wallet aria-hidden="true" />}
          headline={t('budget.empty.title')}
          description={t('budget.empty.description')}
          actionLabel={t('budget.add')}
          onAction={openAdd}
        />
      </Card>
    );
  } else {
    const data = summary.data;
    body = (
      <>
        <Section title={t('budget.section.overview')} description={
            !data.isCurrentYear ? undefined
              : (data.forecastBasisMonths ?? 0) < 3 ? t('budget.section.overviewForecastEarly')
                : t('budget.section.overviewForecast', { count: data.forecastBasisMonths ?? 0 })
          }>
          <BudgetKpis summary={data} />
        </Section>

        <Section title={t('budget.section.monthly')} description={t('budget.section.monthlyDescription')}>
          <Card>
            <MonthlyCategoryChart summary={data} categories={usedCategories} caption={t('budget.charts.monthlyCaption', { year })} />
          </Card>
        </Section>

        <Section title={t('budget.section.comparison')} description={t('budget.section.comparisonDescription', { year, previous: year - 1 })}>
          <Stack gap="6">
            <div className="budget-page__chart-grid">
              <Card>
                <YearComparisonChart summary={data} caption={t('budget.charts.comparisonCaption', { year, previous: year - 1 })} />
              </Card>
              <Card>
                <CumulativeForecastChart
                  summary={data}
                  caption={t(data.isCurrentYear ? 'budget.charts.cumulativeForecastCaption' : 'budget.charts.cumulativeCaption', { year, previous: year - 1 })}
                />
              </Card>
            </div>
            <Card>
              <MonthTable summary={data} />
            </Card>
          </Stack>
        </Section>

        <Section title={t('budget.section.categories')} description={t('budget.section.categoriesDescription')}>
          <Stack gap="6">
            <Card>
              <Stack gap="4">
                <ul className="budget-category-filter" aria-label={t('budget.charts.filterLabel')}>
                  {usedCategories.map((c) => (
                    <li key={c}>
                      <Button size="sm" variant={hidden.has(c) ? 'ghost' : 'secondary'} aria-pressed={!hidden.has(c)} onClick={() => toggleCategory(c)}>
                        <span className="budget-page__filter-label">
                          <CategorySwatch category={c} />
                          {t(`budget.categories.${c}`)}
                        </span>
                      </Button>
                    </li>
                  ))}
                </ul>
                {trendCategories.length > 0 ? (
                  <CategoryTrendChart summary={data} categories={trendCategories} caption={t('budget.charts.trendCaption', { year, previous: year - 1 })} />
                ) : (
                  <Text color="muted">{t('budget.charts.noCategorySelected')}</Text>
                )}
              </Stack>
            </Card>
            <Card>
              <CategoryTable summary={data} categories={usedCategories} />
            </Card>
          </Stack>
        </Section>
      </>
    );
  }

  let expenseBody;
  if (expenses.isPending) {
    expenseBody = <Text color="muted">{t('budget.loading')}</Text>;
  } else if (expenses.isError) {
    expenseBody = (
      <LoadErrorState title={t('budget.expenses.loadError')} onRetry={() => void expenses.refetch()} retryLabel={t('budget.retry')} titleLevel={3} />
    );
  } else if (expenses.data.length === 0) {
    expenseBody = (
      <EmptyState
        icon={<Wallet aria-hidden="true" />}
        headline={t('budget.expenses.emptyTitle', { year })}
        description={t('budget.expenses.emptyDescription')}
        actionLabel={t('budget.add')}
        onAction={openAdd}
        actionVariant="secondary"
        headingLevel={3}
      />
    );
  } else {
    expenseBody = (
      <ExpenseList
        expenses={expenses.data}
        deleting={remove.isPending}
        onEdit={(e: BudgetExpense) => openDialog({ kind: 'expense', expense: e })}
        onDelete={(e) => remove.mutate(e)}
      />
    );
  }

  let recurringBody;
  if (recurring.isPending) {
    recurringBody = <Text color="muted">{t('budget.loading')}</Text>;
  } else if (recurring.isError) {
    recurringBody = (
      <LoadErrorState title={t('budget.recurring.loadError')} onRetry={() => void recurring.refetch()} retryLabel={t('budget.retry')} titleLevel={3} />
    );
  } else if (recurring.data.length === 0) {
    recurringBody = (
      <EmptyState
        icon={<Repeat aria-hidden="true" />}
        headline={t('budget.recurring.emptyTitle')}
        description={t('budget.recurring.emptyDescription')}
        actionLabel={t('budget.recurring.add')}
        onAction={openAddRecurring}
        actionVariant="secondary"
        headingLevel={3}
      />
    );
  } else {
    recurringBody = (
      <RecurringList
        items={recurring.data}
        today={today}
        busy={recurringChange.isPending}
        onEdit={(item) => openDialog({ kind: 'recurring', recurring: item })}
        onEnd={(item) => recurringChange.mutate({ item, action: 'end' })}
        onDelete={(item) => recurringChange.mutate({ item, action: 'delete' })}
      />
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="wide">
          <PageHeader title={t('budget.title')} description={t('budget.subtitle')} actions={<Button onClick={openAdd}>{t('budget.add')}</Button>} />
          <LiveRegion visuallyHidden>{announcement}</LiveRegion>
          <div className="budget-page__toolbar">
            <div className="budget-page__year">
              <Select
                id="budget-year"
                label={t('budget.year')}
                value={String(year)}
                options={yearOptions}
                onChange={(e) => setYear(Number(e.target.value))}
              />
            </div>
          </div>
          {body}
          {energy.data?.configured && (
            <Section title={t('controller.energy.budgetTitle')} description={t('controller.energy.budgetDescription')}>
              <EnergyOverview aquariumId={aquariumId} year={year} manageHref={`/aquariums/${aquariumId}/integrations/controller`} />
            </Section>
          )}
          <Section
            title={t('budget.section.recurring')}
            description={t('budget.section.recurringDescription')}
            actions={
              (recurring.data?.length ?? 0) > 0 ? (
                <Button variant="secondary" onClick={openAddRecurring}>
                  {t('budget.recurring.add')}
                </Button>
              ) : undefined
            }
          >
            <Card>
              <Stack gap="4">
                {recurringChange.isError && <Alert>{t('budget.recurring.changeError')}</Alert>}
                {recurringBody}
              </Stack>
            </Card>
          </Section>
          {(usedCategories.length > 0 || (expenses.data?.length ?? 0) > 0) && (
            <Section title={t('budget.section.expenses', { year })}>
              <Card>
                <Stack gap="4">
                  {remove.isError && <Alert>{t('budget.expenses.deleteError')}</Alert>}
                  {expenseBody}
                </Stack>
              </Card>
            </Section>
          )}
          <ExpenseDialog
            aquariumId={aquariumId}
            isOpen={dialogOpen}
            target={target}
            onClose={() => setDialogOpen(false)}
            onSaved={(saved) => {
              setDialogOpen(false);
              const key = saved.recurring
                ? saved.created ? 'budget.recurring.added' : 'budget.recurring.updated'
                : saved.created ? 'budget.expenses.added' : 'budget.expenses.updated';
              setAnnouncement(t(key, { amount: money.format(saved.amountCents) }));
              const savedYear = saved.spentOn ? Number(saved.spentOn.slice(0, 4)) : year;
              if (savedYear !== year) setYear(savedYear);
              void queryClient.invalidateQueries({ queryKey: budgetKey(aquariumId) });
            }}
          />
        </PageContent>
      }
    />
  );
}

export default BudgetPage;
